import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CalculatorV3StateService, FrameId } from '../services/calculator-v3-state.service';
import { CaseApiService } from '../../../core/services/case-api.service';
import { CaseContextService } from '../../../core/services/case-context.service';
import { Case } from '../../../core/services/case.model';

interface FrameItem {
  id: FrameId;
  num: string;
  label: string;
}

/**
 * Left rail for /calculator-v3. Hosts the F1–F9 frame navigator that
 * previously lived as an in-page top strip. Mounted via the route's
 * `data.rail` and only rendered by RailHostComponent when the menu-axis
 * shell is active; the in-page strip stays for the legacy shell.
 */
@Component({
  selector: 'app-calculator-v3-rail',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="rail-content">

      <div class="rail-section">
        <div class="v3-badge-row">
          <span class="v3-badge">DEV PREVIEW</span>
        </div>
        <div class="rail-section-sub">Live IA data · pick a case below to run F2→F9. Artifacts are mock.</div>
      </div>

      <div class="rail-section">
        <div class="rail-section-h">
          <span class="left">Cases
            @if (cases().length > 0) { <span class="rail-counter blue">{{ cases().length }}</span> }
          </span>
        </div>
        @if (cases().length === 0) {
          <div class="rail-empty">
            No cases yet.
            <div class="rail-empty-sub">Create one in Cases, then load it here.</div>
          </div>
        } @else {
          <div class="case-list">
            @for (c of cases(); track c.id) {
              <button type="button" class="case-card"
                      [class.active]="activeCaseId() === c.id"
                      (click)="onCaseClick(c)">
                <span class="case-id">{{ c.caseCode }}</span>
                <span class="client">{{ c.name }}</span>
                <span class="meta"><span class="dot"></span>{{ c.stage }}</span>
              </button>
            }
          </div>
        }
      </div>

      <div class="rail-section">
        <div class="rail-section-h"><span class="left">Frames</span></div>
        <div class="frame-list">
          @for (f of frames; track f.id) {
            <button type="button" class="frame-card"
                    [class.active]="state.frame() === f.id"
                    [attr.aria-current]="state.frame() === f.id"
                    (click)="state.goTo(f.id)">
              <span class="num">{{ f.num }}</span>
              <span class="label">{{ f.label }}</span>
            </button>
          }
        </div>
      </div>

      <div class="rail-section">
        <button class="reset-btn" type="button" (click)="state.reset()" title="Reset all state">
          ↺ Reset all state
        </button>
        <div class="sibling-links">
          <a class="sibling-link" href="/calculator">/calculator</a>
          <a class="sibling-link" href="/calculator-v2">/calculator-v2</a>
        </div>
      </div>

    </div>
  `,
  styles: [`
    :host { display: contents; }

    .rail-content { display: flex; flex-direction: column; height: 100%; }
    .rail-section { padding: 8px 14px; }
    .rail-section + .rail-section { border-top: 1px solid var(--border-light); }
    .rail-section-h {
      height: 26px; display: flex; align-items: center; justify-content: space-between;
      font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600;
      color: var(--muted); user-select: none; padding: 0 2px; margin-bottom: 4px;
    }
    .rail-section-h .left { display: flex; align-items: center; gap: 6px; }
    .rail-section-sub {
      margin-top: 6px; font-size: 10px; font-weight: 400; font-style: italic;
      color: var(--muted);
    }

    .v3-badge-row { display: flex; }
    .v3-badge {
      font-family: 'Roboto Mono', ui-monospace, monospace; font-size: 9px; font-weight: 700;
      letter-spacing: 0.08em; color: var(--amber, #C58A1A);
      border: 1px solid var(--amber, #C58A1A); border-radius: 3px; padding: 2px 6px;
    }

    .frame-list { display: flex; flex-direction: column; gap: 4px; margin-top: 4px; }
    .frame-card {
      display: flex; align-items: center; gap: 8px; width: 100%;
      background: var(--panel); border: 1px solid var(--border-light); border-radius: 3px;
      padding: 6px 8px; cursor: pointer; font-family: inherit; text-align: left;
      transition: background 0.12s, border-color 0.12s;
    }
    .frame-card:hover { background: var(--bg); border-color: var(--border); }
    .frame-card.active {
      background: var(--blue-soft); border-color: var(--blue);
      border-left: 3px solid var(--blue); padding-left: 6px;
    }
    .frame-card.active .label { color: var(--navy); }
    .frame-card .num {
      font-family: 'Roboto Mono', ui-monospace, monospace; font-size: 9px; font-weight: 700;
      color: var(--muted); flex-shrink: 0; min-width: 16px;
    }
    .frame-card.active .num { color: var(--blue); }
    .frame-card .label {
      font-size: 12px; font-weight: 600; color: var(--text2);
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }

    .reset-btn {
      width: 100%; height: 32px; background: var(--panel); border: 1px solid var(--border);
      border-radius: 4px; font-family: inherit; font-size: 12px; font-weight: 600;
      color: var(--muted); cursor: pointer; transition: background 0.12s, color 0.12s, border-color 0.12s;
    }
    .reset-btn:hover { background: var(--bg); color: var(--neg, #D04A4A); border-color: var(--neg, #D04A4A); }
    .sibling-links { display: flex; gap: 10px; margin-top: 8px; justify-content: center; }
    .sibling-link {
      font-family: 'Roboto Mono', ui-monospace, monospace; font-size: 10px; font-weight: 600;
      color: var(--blue); text-decoration: none;
    }
    .sibling-link:hover { text-decoration: underline; }

    .rail-counter {
      font-family: 'Roboto Mono', ui-monospace, monospace; font-size: 10px; font-weight: 500;
      padding: 1px 6px; border-radius: 8px; min-width: 22px; text-align: center;
      border: 1px solid var(--border-light); background: var(--panel); color: var(--text2);
    }
    .rail-counter.blue { background: var(--blue-soft); color: var(--blue); border-color: var(--blue-border, var(--border)); }
    .case-list { display: flex; flex-direction: column; gap: 4px; margin-top: 4px; }
    .case-card {
      display: flex; flex-direction: column; gap: 2px; width: 100%; text-align: left;
      background: var(--panel); border: 1px solid var(--border-light); border-radius: 3px;
      padding: 6px 8px; cursor: pointer; font-family: inherit;
      transition: background 0.12s, border-color 0.12s;
    }
    .case-card:hover { background: var(--bg); border-color: var(--border); }
    .case-card.active {
      background: var(--blue-soft); border-color: var(--blue);
      border-left: 3px solid var(--blue); padding-left: 6px;
    }
    .case-card .case-id {
      font-family: 'Roboto Mono', ui-monospace, monospace; font-size: 9px; font-weight: 600;
      color: var(--muted); letter-spacing: 0.04em;
    }
    .case-card .client {
      font-size: 12px; font-weight: 600; color: var(--navy);
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .case-card .meta {
      display: flex; align-items: center; gap: 5px;
      font-size: 10px; font-weight: 500; color: var(--muted);
    }
    .case-card .meta .dot { width: 6px; height: 6px; border-radius: 50%; background: var(--muted); flex-shrink: 0; }
    .rail-empty {
      padding: 10px 8px; font-size: 11px; color: var(--muted); font-weight: 500; text-align: center;
      border: 1px dashed var(--border-light); border-radius: 4px; margin-top: 4px;
    }
    .rail-empty .rail-empty-sub { margin-top: 4px; font-size: 10px; font-weight: 400; font-style: italic; }
  `],
})
export class CalculatorV3RailComponent implements OnInit, OnDestroy {
  readonly state = inject(CalculatorV3StateService);
  private readonly caseApi = inject(CaseApiService);
  private readonly caseCtx = inject(CaseContextService);

  protected readonly cases = signal<Case[]>([]);
  protected readonly activeCaseId = computed(() => this.caseCtx.activeCase()?.id ?? null);

  ngOnInit(): void {
    this.loadCases();
    window.addEventListener('focus', this.loadCases);
  }

  ngOnDestroy(): void {
    window.removeEventListener('focus', this.loadCases);
  }

  private loadCases = (): void => {
    this.caseApi.list().subscribe({
      next: (rows) => this.cases.set(rows),
      error: () => this.cases.set([]),
    });
  };

  onCaseClick(c: Case): void {
    this.caseCtx.setActive(c);
    this.state.loadCase();
  }

  readonly frames: ReadonlyArray<FrameItem> = [
    { id: 'f1-empty',       num: 'F1', label: 'Empty state' },
    { id: 'f2-case',        num: 'F2', label: 'Case loaded · partial' },
    { id: 'f3-calculated',  num: 'F3', label: 'Calculated · result' },
    { id: 'f4-comparison',  num: 'F4', label: 'Comparison expanded' },
    { id: 'f5-illustrate',  num: 'F5', label: '→ V2 Illustrate' },
    { id: 'f6-compare',     num: 'F6', label: '→ V2 Compare' },
    { id: 'f7-deliver',     num: 'F7', label: '→ V2 Deliver' },
    { id: 'f8-profile',     num: 'F8', label: 'Profile flyout' },
    { id: 'f9-save',        num: 'F9', label: 'Save scenario' },
  ];
}
